// Client-side detection of INPUT/OUTPUT ports from spreadsheet cells.
// Mirrors what the engine would discover after parsing so the editor
// renders handles instantly. Outputs are now primarily declared via
// the explicit `outputs` config; we still scan OUTPUT() in cells for
// backward compatibility.
const INPUT_RE = /\bINPUT\s*\(\s*"([^"]*)"\s*\)/g;
const OUTPUT_RE = /\bOUTPUT\s*\(\s*"([^"]*)"\s*,/g;
/* ---------------------------------------------------------------------------
 * Cell-reference parsing & manipulation
 * -------------------------------------------------------------------------
 *
 * The engine accepts A1-style references with optional `$` anchors:
 *   A1 / $A1 / A$1 / $A$1   — single cell
 *   A1:B5 / $A$1:B$5         — range (each endpoint independently anchored)
 *
 * These helpers find every reference in a formula and apply a transform
 * that respects the anchors. They power Insert Row/Col, Delete Row/Col,
 * and the relative-paste path. We deliberately ignore strings inside
 * double quotes so a literal "A1" inside a formula text value isn't
 * mangled.
 */
// Single A1 ref: optional `$`, 1-3 letters, optional `$`, 1-7 digits.
// (We bound col letters and row digits to defang regex catastrophic
// backtracking; real spreadsheets never need more.)
const CELL_RE = /(\$?)([A-Z]{1,3})(\$?)(\d{1,7})/g;
// Detect strings (so we can mask them out before substituting refs).
const STRING_RE = /"(?:[^"\\]|\\.)*"/g;
export function colToIndex(col) {
    let n = 0;
    for (const ch of col.toUpperCase())
        n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
}
export function indexToCol(idx) {
    let s = "";
    let n = idx + 1;
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}
export function parseCellRef(ref) {
    const m = /^(\$?)([A-Z]{1,3})(\$?)(\d{1,7})$/i.exec(ref.trim());
    if (!m)
        return null;
    return {
        colAbs: !!m[1],
        col: m[2].toUpperCase(),
        rowAbs: !!m[3],
        row: parseInt(m[4], 10),
    };
}
/**
 * Walk every cell ref in `formula` and apply `fn`. Anchored components
 * (`$A` or `A$1`) are passed through verbatim; the transform decides
 * what to do with the un-anchored components. Returning null from `fn`
 * substitutes `#REF!` for that ref (used when a referenced row/col is
 * deleted).
 *
 * Preserves whitespace, function names, operators — only the matched
 * cell references are touched. Strings are masked out first so e.g.
 * `="A1 is "&A1` only shifts the trailing A1.
 */
export function transformFormulaRefs(formula, fn) {
    if (!formula)
        return formula;
    // Mask strings with placeholders. The placeholder uses non-letter
    // characters so it can't be parsed as a cell ref (`STRn` would be
    // — `STR` is a valid 3-letter column).
    const stringSlots = [];
    const masked = formula.replace(STRING_RE, (s) => {
        stringSlots.push(s);
        return `\x01_${stringSlots.length - 1}_\x01`;
    });
    const replaced = masked.replace(CELL_RE, (_full, colAbs, col, rowAbs, rowDigits) => {
        const next = fn({
            col: col.toUpperCase(),
            row: parseInt(rowDigits, 10),
            colAbs: colAbs === "$",
            rowAbs: rowAbs === "$",
        });
        if (!next)
            return "#REF!";
        return ((next.colAbs ? "$" : "")
            + next.col
            + (next.rowAbs ? "$" : "")
            + String(next.row));
    });
    return replaced.replace(/\x01_(\d+)_\x01/g, (_m, idx) => stringSlots[Number(idx)] ?? "");
}
/**
 * Insert `count` rows at `before` (1-based). Refs at row >= `before`
 * shift down by `count`, except those with a `$row` anchor (those stay).
 * Cell IDs themselves also need re-keying — that's the caller's job.
 */
export function shiftFormulaForRowInsert(formula, before, count = 1) {
    return transformFormulaRefs(formula, (r) => {
        if (r.rowAbs || r.row < before)
            return r;
        return { ...r, row: r.row + count };
    });
}
export function shiftFormulaForRowDelete(formula, at, count = 1) {
    return transformFormulaRefs(formula, (r) => {
        if (r.row < at)
            return r;
        if (r.row >= at && r.row < at + count)
            return null; // → #REF!
        if (r.rowAbs)
            return r; // anchored row past the deletion shifts too
        return { ...r, row: r.row - count };
    });
}
export function shiftFormulaForColInsert(formula, beforeIdx, count = 1) {
    return transformFormulaRefs(formula, (r) => {
        const ci = colToIndex(r.col);
        if (r.colAbs || ci < beforeIdx)
            return r;
        return { ...r, col: indexToCol(ci + count) };
    });
}
export function shiftFormulaForColDelete(formula, atIdx, count = 1) {
    return transformFormulaRefs(formula, (r) => {
        const ci = colToIndex(r.col);
        if (ci < atIdx)
            return r;
        if (ci >= atIdx && ci < atIdx + count)
            return null;
        if (r.colAbs)
            return r;
        return { ...r, col: indexToCol(ci - count) };
    });
}
/**
 * Relative-paste shift: when a cell is copied from `src` to `dst`,
 * every non-anchored row/col component in its formula moves by the
 * (dst − src) delta. Anchored components stay put. Mirrors Excel's
 * fill-handle behaviour.
 */
export function shiftFormulaForRelativePaste(formula, src, dst) {
    const dCol = colToIndex(dst.col) - colToIndex(src.col);
    const dRow = dst.row - src.row;
    if (dCol === 0 && dRow === 0)
        return formula;
    return transformFormulaRefs(formula, (r) => {
        let { col, row } = r;
        if (!r.colAbs) {
            const ci = colToIndex(col);
            if (ci + dCol < 0)
                return null;
            col = indexToCol(ci + dCol);
        }
        if (!r.rowAbs) {
            if (row + dRow < 1)
                return null;
            row = row + dRow;
        }
        return { ...r, col, row };
    });
}
/**
 * Cycle a cell ref's anchor state, like Excel's F4 key:
 *   A1 → $A$1 → A$1 → $A1 → A1 → …
 * The ref must be a valid A1-style reference; otherwise returns the
 * input unchanged.
 */
export function cycleAnchor(ref) {
    const p = parseCellRef(ref);
    if (!p)
        return ref;
    // States: 0=neither, 1=both, 2=row only, 3=col only.
    const state = !p.colAbs && !p.rowAbs ? 0
        : p.colAbs && p.rowAbs ? 1
            : !p.colAbs && p.rowAbs ? 2
                : 3;
    const next = (state + 1) % 4;
    const colAbs = next === 1 || next === 3;
    const rowAbs = next === 1 || next === 2;
    return ((colAbs ? "$" : "") + p.col + (rowAbs ? "$" : "") + p.row);
}
export function detectSpreadsheetPorts(cells, outputsConfig) {
    const inputs = new Set();
    const outputs = new Set();
    if (cells) {
        for (const v of Object.values(cells)) {
            if (typeof v !== "string")
                continue;
            INPUT_RE.lastIndex = 0;
            OUTPUT_RE.lastIndex = 0;
            let m;
            while ((m = INPUT_RE.exec(v)) !== null)
                inputs.add(m[1]);
            while ((m = OUTPUT_RE.exec(v)) !== null)
                outputs.add(m[1]);
        }
    }
    for (const o of outputsConfig ?? []) {
        const p = (o?.port ?? "").trim();
        if (p)
            outputs.add(p);
    }
    return {
        inputs: Array.from(inputs).sort(),
        outputs: Array.from(outputs).sort(),
    };
}
